/**
 * Unix timestamp parsing and rendering for the /timestamp tool.
 *
 * The hard part is not the arithmetic, it is guessing the unit: APIs hand back
 * seconds, milliseconds and microseconds with no label. The magnitude of the
 * number is the only signal available, so that rule lives here in one place,
 * is reported back to the user, and is unit-tested.
 */

export type EpochUnit = 'seconds' | 'milliseconds' | 'microseconds';

export interface ParsedInstant {
  date: Date;
  /** How the input was read: the epoch unit, or `iso` for a parsed date string. */
  unit: EpochUnit | 'iso';
  /** What the tool understood, phrased for the UI. */
  interpretation: string;
}

export class TimestampError extends Error {
  constructor(message: string) {
    super(message);
    this.name = 'TimestampError';
  }
}

/**
 * Digit-count thresholds. Present-day epoch values are 10 digits in seconds,
 * 13 in milliseconds and 16 in microseconds; the boundaries sit between those
 * ranges so an older or future date still lands on the right unit.
 */
export function detectUnit(value: number): EpochUnit {
  const magnitude = Math.abs(value);
  if (magnitude >= 1e14) {
    return 'microseconds';
  }
  if (magnitude >= 1e11) {
    return 'milliseconds';
  }
  return 'seconds';
}

const UNIT_MILLIS: Record<EpochUnit, number> = {
  seconds: 1000,
  milliseconds: 1,
  microseconds: 1 / 1000,
};

const UNIT_LABELS: Record<EpochUnit, string> = {
  seconds: 'epoch seconds',
  milliseconds: 'epoch milliseconds',
  microseconds: 'epoch microseconds',
};

/**
 * Read an epoch number (unit auto-detected, or forced) or a date string.
 *
 * Throws `TimestampError` with user-facing copy rather than returning null, so
 * the page can put the message straight in the error slot.
 */
export function parseInstant(input: string, forcedUnit?: EpochUnit): ParsedInstant {
  const trimmed = input.trim();
  if (!trimmed) {
    throw new TimestampError('Enter a timestamp or a date to convert.');
  }

  if (/^-?\d+(\.\d+)?$/.test(trimmed)) {
    const value = Number(trimmed);
    if (!Number.isFinite(value)) {
      throw new TimestampError('That number is too large to be a timestamp.');
    }
    const unit = forcedUnit ?? detectUnit(value);
    const date = new Date(value * UNIT_MILLIS[unit]);
    if (Number.isNaN(date.getTime())) {
      throw new TimestampError('That number is outside the range of representable dates.');
    }
    return {
      date,
      unit,
      interpretation: `Read as ${UNIT_LABELS[unit]}${forcedUnit ? '' : ' (detected from the number of digits)'}.`,
    };
  }

  const parsed = new Date(trimmed);
  if (Number.isNaN(parsed.getTime())) {
    throw new TimestampError(
      'Could not read that as a date. Try an epoch number or an ISO 8601 string such as 2018-01-18T01:30:22Z.',
    );
  }
  return {
    date: parsed,
    unit: 'iso',
    interpretation: /(Z|[+-]\d{2}:?\d{2})$/.test(trimmed)
      ? 'Read as a date string with an explicit time zone offset.'
      : 'Read as a date string with no time zone, so your local time zone was assumed.',
  };
}

/** Epoch seconds for a date, truncated toward the epoch like every other tool. */
export function toEpochSeconds(date: Date): number {
  return Math.floor(date.getTime() / 1000);
}

/** "3 days ago" / "in 2 hours" — the distance from `now`, in the largest useful unit. */
export function relativeToNow(date: Date, now: number = Date.now()): string {
  const deltaSeconds = Math.round((date.getTime() - now) / 1000);
  const magnitude = Math.abs(deltaSeconds);

  if (magnitude < 5) {
    return 'just now';
  }

  const units: Array<[number, string]> = [
    [60, 'second'],
    [60, 'minute'],
    [24, 'hour'],
    [7, 'day'],
    [4.348, 'week'],
    [12, 'month'],
    [Number.POSITIVE_INFINITY, 'year'],
  ];

  let value = magnitude;
  let label = 'second';
  for (const [step, name] of units) {
    label = name;
    if (value < step) {
      break;
    }
    value /= step;
  }

  const rounded = Math.round(value);
  const plural = rounded === 1 ? '' : 's';
  return deltaSeconds < 0 ? `${rounded} ${label}${plural} ago` : `in ${rounded} ${label}${plural}`;
}

export interface TimestampRendering {
  label: string;
  value: string;
  /** Marks the rows that are worth copying as-is. */
  copyable: boolean;
}

/**
 * Every rendering of one instant, in the order the tool lists them. Local
 * strings come from `toLocaleString`, so they follow the visitor's own locale.
 */
export function renderInstant(date: Date, now: number = Date.now()): TimestampRendering[] {
  const millis = date.getTime();
  return [
    { label: 'ISO 8601 (UTC)', value: date.toISOString(), copyable: true },
    { label: 'UTC', value: date.toUTCString(), copyable: true },
    { label: 'Local time', value: date.toLocaleString(), copyable: true },
    {
      label: 'Time zone',
      value: Intl.DateTimeFormat().resolvedOptions().timeZone || 'unknown',
      copyable: false,
    },
    { label: 'Epoch seconds', value: String(Math.floor(millis / 1000)), copyable: true },
    { label: 'Epoch milliseconds', value: String(millis), copyable: true },
    { label: 'Relative', value: relativeToNow(date, now), copyable: false },
  ];
}
